import React from "react";
import Icon from "../../../components/AppIcon";
import { useTranslation } from 'react-i18next';

const CheckoutProgress = ({ currentStep = 1, className = '' }) => {
  const { t } = useTranslation();
  
  const steps = [
    {
      id: 1,
      title: t('shipping'),
      description: t('deliveryInformation'),
      icon: 'Truck'
    },
    {
      id: 2,
      title: t('payment'),
      description: t('paymentMethod'),
      icon: 'CreditCard'
    },
    {
      id: 3,
      title: t('review'),
      description: t('orderConfirmation'),
      icon: 'CheckCircle'
    }
  ];

  const getStepStatus = (stepId) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'upcoming';
  };

  const getStepClasses = (status) => {
    switch (status) {
      case 'completed':
        return {
          container: 'text-success',
          circle: 'bg-success border-success text-white',
          line: 'bg-success'
        };
      case 'current':
        return {
          container: 'text-primary',
          circle: 'bg-primary border-primary text-white',
          line: 'bg-border'
        };
      default:
        return {
          container: 'text-text-secondary',
          circle: 'bg-surface border-border text-text-secondary',
          line: 'bg-border'
        };
    }
  };

  return (
    <div className={`${className}`}>
      {/* Desktop Progress Bar */}
      <div className="hidden md:block">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const status = getStepStatus(step.id);
            const classes = getStepClasses(status);
            const isLast = index === steps.length - 1;

            return (
              <div key={step.id} className="flex items-center flex-1">
                <div className={`flex items-center ${classes.container}`}>
                  {/* Step Circle */}
                  <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${classes.circle} transition-smooth`}>
                    {status === 'completed' ? (
                      <Icon name="Check" size={18} />
                    ) : (
                      <Icon name={step.icon} size={18} />
                    )}
                  </div>

                  {/* Step Info */}
                  <div className="ml-3">
                    <div className="text-sm font-medium">{step.title}</div>
                    <div className="text-xs opacity-75">{step.description}</div>
                  </div>
                </div>

                {/* Connecting Line */}
                {!isLast && (
                  <div className="flex-1 mx-4">
                    <div className={`h-0.5 ${classes.line} transition-smooth`}></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile Progress Bar */}
      <div className="md:hidden">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-medium text-text-primary">
            {t('step')} {currentStep} {t('of')} {steps.length}
          </div>
          <div className="text-sm text-text-secondary">
            {steps.find(s => s.id === currentStep)?.title}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-border rounded-full h-2 mb-4">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300 ease-in-out"
            style={{ width: `${(currentStep / steps.length) * 100}%` }}
          ></div>
        </div>

        {/* Current Step Info */}
        <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
            <Icon 
              name={steps.find(s => s.id === currentStep)?.icon} 
              size={16} 
              className="text-white" 
            />
          </div>
          <div>
            <div className="font-medium text-text-primary">
              {steps.find(s => s.id === currentStep)?.title}
            </div>
            <div className="text-sm text-text-secondary">
              {steps.find(s => s.id === currentStep)?.description}
            </div>
          </div>
        </div>
      </div>

      {/* Step Navigation Dots (Mobile Alternative) */}
      <div className="md:hidden flex justify-center space-x-2 mt-4">
        {steps.map((step) => {
          const status = getStepStatus(step.id);
          return (
            <div
              key={step.id}
              className={`w-2 h-2 rounded-full transition-smooth ${
                status === 'completed' 
                  ? 'bg-success' 
                  : status === 'current' ?'bg-primary' :'bg-border'
              }`}
            />
          );
        })}
      </div>
    </div>
  );
};

export default CheckoutProgress;
